/**
 * Server state, all of it.
 *
 * Nothing in this app holds server data in `useState`. Every read goes through
 * React Query so that caching, refetching and invalidation are decided once
 * here rather than differently in each component.
 */
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import {
  apiFetch,
  isUnauthenticated,
  type ActivityWire,
  type BalancesWire,
  type CardWire,
  type ConnectionWire,
  type EarningsWire,
  type EnrolmentIntentWire,
  type ExplanationWire,
  type OfferWire,
  type PayoutEligibilityWire,
  type PayoutWire,
  type PayoutDestinationWire,
  type PayoutMethodInfoWire,
  type RedemptionEligibilityWire,
  type RedemptionWire,
  type ReferralCodeWire,
  type SessionWire,
  type SplitTermsWire,
  statementSurface,
  type StatementKind,
  type StatementWire,
  type TaxSummaryWire,
  type TrackedLinkWire,
} from './client.js';

export const keys = {
  session: ['session'] as const,
  cashback: ['members', 'me', 'cashback'] as const,
  transactions: ['members', 'me', 'transactions'] as const,
  cards: ['members', 'me', 'cards'] as const,
  memberConnections: ['members', 'me', 'connections'] as const,
  offers: ['offers'] as const,
  earnings: ['creators', 'me', 'earnings'] as const,
  creatorActivity: ['creators', 'me', 'activity'] as const,
  referralCodes: ['creators', 'me', 'referral-codes'] as const,
  creatorConnections: ['creators', 'me', 'connections'] as const,
  payoutEligibility: ['creators', 'me', 'payouts', 'eligibility'] as const,
  payouts: ['creators', 'me', 'payouts'] as const,
  statements: (kind: StatementKind) => [statementSurface(kind), 'me', 'statements'] as const,
  redemptionEligibility: ['members', 'me', 'redemptions', 'eligibility'] as const,
  redemptions: ['members', 'me', 'redemptions'] as const,
  payoutDestinations: ['me', 'payout-destinations'] as const,
  payoutMethods: ['me', 'payout-methods'] as const,
  taxInformation: ['creators', 'me', 'tax-information'] as const,
  links: ['creators', 'me', 'links'] as const,
};

/**
 * The session. `null` means signed out.
 *
 * Signed-out is a *value*, not an error. Modelling it as a rejected query meant
 * the UI depended on a refetch completing to notice a logout — and when that
 * refetch was aborted, React Query kept the previous successful data and the
 * dashboard stayed on screen after the server had already destroyed the
 * session. Returning `null` makes the state explicit and settable directly.
 *
 * `retry: false` still matters: a 401 is not worth three attempts.
 */
export function useSession(): UseQueryResult<SessionWire | null> {
  return useQuery({
    queryKey: keys.session,
    queryFn: async ({ signal }) => {
      try {
        return await apiFetch<SessionWire>('/v1/auth/me', { signal });
      } catch (error) {
        if (isUnauthenticated(error)) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 60_000,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      apiFetch<SessionWire>('/v1/auth/login', { method: 'POST', body: input }),
    onSuccess: (session) => {
      queryClient.setQueryData(keys.session, session);
      // A different principal sees entirely different data.
      void queryClient.invalidateQueries();
    },
  });
}

export function useRegister() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      email: string;
      password: string;
      displayName: string;
      kind: 'member' | 'creator';
    }) => apiFetch<SessionWire>('/v1/auth/register', { method: 'POST', body: input }),
    onSuccess: (session) => {
      queryClient.setQueryData(keys.session, session);
      void queryClient.invalidateQueries();
    },
  });
}

/**
 * Sign out, and discard everything.
 *
 * This deliberately reloads the page rather than reconciling the cache.
 *
 * Two earlier attempts — `queryClient.clear()`, then `removeQueries()` plus an
 * explicit `setQueryData(session, null)` — both left the dashboard on screen
 * after the server had destroyed the session, because the mutation callback
 * that was supposed to do the clearing never ran. A signed-out user looking at
 * their own balances is not a cosmetic bug, and "the cache should have been
 * cleared" is not a property worth trusting to callback ordering.
 *
 * A reload is the one thing that cannot half-work: no observers, no retained
 * closures, no cached responses. Sign-out is also the single action where
 * throwing away all client state *is* the intent, so the usual objection to a
 * full reload does not apply.
 */
export function useLogout() {
  return useMutation({
    mutationFn: () => apiFetch<void>('/v1/auth/logout', { method: 'POST' }),
    // `onSettled`: if the request failed because the session was already gone,
    // the user still asked to leave and must end up signed out.
    onSettled: () => {
      window.location.replace('/');
    },
  });
}

/** The fan/creator toggle. */
export function useSwitchProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (principalId: string) =>
      apiFetch<SessionWire>('/v1/auth/switch-profile', {
        method: 'POST',
        body: { principalId },
      }),
    onSuccess: (session) => {
      queryClient.setQueryData(keys.session, session);
      void queryClient.invalidateQueries();
    },
  });
}

/* ---- member ------------------------------------------------------- */

export function useCashback(enabled: boolean): UseQueryResult<BalancesWire> {
  return useQuery({
    queryKey: keys.cashback,
    queryFn: ({ signal }) => apiFetch<BalancesWire>('/v1/members/me/cashback', { signal }),
    enabled,
  });
}

export function useTransactions(enabled: boolean): UseQueryResult<ActivityWire> {
  return useQuery({
    queryKey: keys.transactions,
    queryFn: ({ signal }) => apiFetch<ActivityWire>('/v1/members/me/transactions', { signal }),
    enabled,
  });
}

export function useCards(enabled: boolean): UseQueryResult<CardWire[]> {
  return useQuery({
    queryKey: keys.cards,
    queryFn: ({ signal }) => apiFetch<CardWire[]>('/v1/members/me/cards', { signal }),
    enabled,
  });
}

/**
 * Begin enrolling a card.
 *
 * Takes nothing and returns a reference, not a card. The browser hands that
 * reference to the provider's SDK; the provider confirms over a webhook, and
 * the card appears in `useCards` some moments later.
 *
 * That asynchrony is the visible cost of the security property — the client no
 * longer supplies a card token, so it cannot know the outcome synchronously.
 * The UI polls rather than pretending otherwise.
 */
export function useBeginCardEnrolment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<EnrolmentIntentWire>('/v1/members/me/cards', { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.cards }),
  });
}

export function useRemoveCard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (cardId: string) =>
      apiFetch<void>(`/v1/members/me/cards/${cardId}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.cards }),
  });
}

export function useMemberConnections(enabled: boolean): UseQueryResult<ConnectionWire[]> {
  return useQuery({
    queryKey: keys.memberConnections,
    queryFn: ({ signal }) => apiFetch<ConnectionWire[]>('/v1/members/me/connections', { signal }),
    enabled,
  });
}

export function useConnect() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      apiFetch<ConnectionWire>('/v1/members/me/connections', {
        method: 'POST',
        body: { code },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.memberConnections }),
  });
}

export function useEndConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (connectionId: string) =>
      apiFetch<void>(`/v1/members/me/connections/${connectionId}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.memberConnections }),
  });
}

/* ---- creator ------------------------------------------------------ */

export function useEarnings(enabled: boolean): UseQueryResult<EarningsWire> {
  return useQuery({
    queryKey: keys.earnings,
    queryFn: ({ signal }) => apiFetch<EarningsWire>('/v1/creators/me/earnings', { signal }),
    enabled,
  });
}

export function useCreatorActivity(enabled: boolean): UseQueryResult<ActivityWire> {
  return useQuery({
    queryKey: keys.creatorActivity,
    queryFn: ({ signal }) => apiFetch<ActivityWire>('/v1/creators/me/activity', { signal }),
    enabled,
  });
}

export function useReferralCodes(enabled: boolean): UseQueryResult<ReferralCodeWire[]> {
  return useQuery({
    queryKey: keys.referralCodes,
    queryFn: ({ signal }) =>
      apiFetch<ReferralCodeWire[]>('/v1/creators/me/referral-codes', { signal }),
    enabled,
  });
}

export function useMintReferralCode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { code: string; terms: SplitTermsWire }) =>
      apiFetch<ReferralCodeWire>('/v1/creators/me/referral-codes', {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.referralCodes }),
  });
}

export function useCreatorConnections(enabled: boolean): UseQueryResult<ConnectionWire[]> {
  return useQuery({
    queryKey: keys.creatorConnections,
    queryFn: ({ signal }) => apiFetch<ConnectionWire[]>('/v1/creators/me/connections', { signal }),
    enabled,
  });
}

/* ---- offers -------------------------------------------------------- */

/**
 * Where the signed-in person can earn, with their own rate applied.
 *
 * Longer `staleTime` than balances: a rate card changes when a merchant
 * renegotiates, not when someone spends money, and refetching it on every focus
 * would be noise.
 */
export function useOffers(enabled: boolean): UseQueryResult<OfferWire[]> {
  return useQuery({
    queryKey: keys.offers,
    queryFn: ({ signal }) => apiFetch<OfferWire[]>('/v1/offers', { signal }),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/* ---- payouts ------------------------------------------------------- */

export function usePayoutEligibility(enabled: boolean): UseQueryResult<PayoutEligibilityWire> {
  return useQuery({
    queryKey: keys.payoutEligibility,
    queryFn: ({ signal }) =>
      apiFetch<PayoutEligibilityWire>('/v1/creators/me/payouts/eligibility', { signal }),
    enabled,
  });
}

export function usePayouts(enabled: boolean): UseQueryResult<PayoutWire[]> {
  return useQuery({
    queryKey: keys.payouts,
    queryFn: ({ signal }) => apiFetch<PayoutWire[]>('/v1/creators/me/payouts', { signal }),
    enabled,
  });
}

/**
 * Issued statements (migration 0028).
 *
 * A long `staleTime` because the list only changes once a month, when the
 * generation job runs, and an issued statement can never change at all. This is
 * the one query in the app where refetching on focus would be pure noise.
 *
 * Keyed by kind, so a person holding both a creator and a member account does
 * not see one list cached under the other. That is not hypothetical — §2 has
 * always allowed it, and it is the ordinary case for a creator who also shops.
 */
export function useStatements(
  kind: StatementKind,
  enabled: boolean,
): UseQueryResult<StatementWire[]> {
  return useQuery({
    queryKey: keys.statements(kind),
    queryFn: ({ signal }) =>
      apiFetch<StatementWire[]>(`/v1/${statementSurface(kind)}/me/statements`, { signal }),
    enabled,
    staleTime: 60 * 60_000,
  });
}

export function useRequestPayout() {
  const queryClient = useQueryClient();
  return useMutation({
    /**
     * A destination id as well as the key (migration 0027).
     *
     * A creator's payout used to name no destination at all — the provider was
     * handed their principal id as a funding source, which is not one. There is
     * now somewhere for the money to arrive, and naming it is required.
     */
    mutationFn: (input: { idempotencyKey: string; destinationId: string }) =>
      apiFetch<PayoutWire>('/v1/creators/me/payouts', { method: 'POST', body: input }),
    /**
     * A payout moves money out of `available`, so the earnings figures on
     * screen are stale the moment it succeeds. Invalidating all four rather
     * than patching one keeps the page honest without the UI having to model
     * what the ledger did.
     */
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.payouts });
      void queryClient.invalidateQueries({ queryKey: keys.payoutEligibility });
      void queryClient.invalidateQueries({ queryKey: keys.earnings });
      void queryClient.invalidateQueries({ queryKey: keys.creatorActivity });
    },
  });
}

/**
 * Why one conversion happened.
 *
 * Fetched on demand rather than with the activity list: most rows are never
 * asked about, and joining every explanation into the list would turn a page of
 * history into a page of queries.
 *
 * `staleTime: Infinity` because it genuinely cannot change for a settled
 * conversion, and for a live one the interesting field is `state`, which the
 * activity list already shows. Re-fetching on every expand would be work for
 * nothing.
 */
export function useExplanation(conversionId: string | null): UseQueryResult<ExplanationWire> {
  return useQuery({
    queryKey: ['conversions', conversionId, 'explanation'] as const,
    queryFn: ({ signal }) =>
      apiFetch<ExplanationWire>(`/v1/conversions/${conversionId!}/explanation`, { signal }),
    enabled: conversionId !== null,
    staleTime: Infinity,
  });
}

/* ---- redemptions (the fan's cash out) ------------------------------- */

export function useRedemptionEligibility(
  enabled: boolean,
): UseQueryResult<RedemptionEligibilityWire> {
  return useQuery({
    queryKey: keys.redemptionEligibility,
    queryFn: ({ signal }) =>
      apiFetch<RedemptionEligibilityWire>('/v1/members/me/redemptions/eligibility', { signal }),
    enabled,
  });
}

export function useRedemptions(enabled: boolean): UseQueryResult<RedemptionWire[]> {
  return useQuery({
    queryKey: keys.redemptions,
    queryFn: ({ signal }) => apiFetch<RedemptionWire[]>('/v1/members/me/redemptions', { signal }),
    enabled,
  });
}

export function useRequestRedemption() {
  const queryClient = useQueryClient();
  return useMutation({
    /**
     * A destination id, not a method and a typed-in address.
     *
     * The old body carried `method` and a free-text `destinationHint` that the
     * server recorded and then ignored — the provider was handed the member's
     * own principal id as the destination. Naming a saved row is what makes the
     * money go where the fan chose, and it is why there is no longer a text
     * field here in which an account number could be typed.
     */
    mutationFn: (input: { idempotencyKey: string; destinationId: string }) =>
      apiFetch<RedemptionWire>('/v1/members/me/redemptions', { method: 'POST', body: input }),
    /**
     * A cash out moves money out of `available`, so the cashback figures on
     * screen are stale the moment it succeeds. Invalidating all four rather
     * than patching one keeps the page honest without the UI having to model
     * what the ledger did.
     */
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.redemptions });
      void queryClient.invalidateQueries({ queryKey: keys.redemptionEligibility });
      void queryClient.invalidateQueries({ queryKey: keys.cashback });
      void queryClient.invalidateQueries({ queryKey: keys.transactions });
    },
  });
}

export function useAcceptTerms() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (version: string) =>
      apiFetch<{ version: string; acceptedAt: string }>('/v1/members/me/terms', {
        method: 'POST',
        body: { version },
      }),
    // Eligibility carries `termsToAccept`, so accepting changes what the cash
    // out panel is allowed to show.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.redemptionEligibility });
    },
  });
}

/* ---- payout destinations (shared by fans and creators) -------------- */

export function usePayoutDestinations(enabled: boolean): UseQueryResult<PayoutDestinationWire[]> {
  return useQuery({
    queryKey: keys.payoutDestinations,
    queryFn: ({ signal }) =>
      apiFetch<PayoutDestinationWire[]>('/v1/me/payout-destinations', { signal }),
    enabled,
  });
}

/**
 * What each rail is called and how long it takes.
 *
 * Fetched rather than hardcoded so this client and the mobile one quote the
 * same timings. Two clients disagreeing about how long a bank transfer takes
 * produces a support ticket nobody can resolve, because both are "right".
 */
export function usePayoutMethods(enabled: boolean): UseQueryResult<PayoutMethodInfoWire[]> {
  return useQuery({
    queryKey: keys.payoutMethods,
    queryFn: ({ signal }) => apiFetch<PayoutMethodInfoWire[]>('/v1/me/payout-methods', { signal }),
    enabled,
    // The rails change when a provider contract changes, not while someone is
    // looking at the page.
    staleTime: 60 * 60 * 1000,
  });
}

export function useAddGiftCardDestination() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (email: string) =>
      apiFetch<PayoutDestinationWire>('/v1/me/payout-destinations/gift-card', {
        method: 'POST',
        body: { email },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.payoutDestinations });
    },
  });
}

/**
 * Begin linking a bank account.
 *
 * Returns a reference for the processor's hosted flow and nothing else — no
 * destination exists yet. It appears in `usePayoutDestinations` once the
 * processor confirms the link over a signed webhook, which is why the panel
 * that calls this waits rather than optimistically adding a row.
 */
export function useBeginBankLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ verificationRef: string; expiresAt: string }>(
        '/v1/me/payout-destinations/bank-link',
        { method: 'POST' },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.payoutDestinations });
    },
  });
}

export function useRemovePayoutDestination() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (destinationId: string) =>
      apiFetch<void>(`/v1/me/payout-destinations/${destinationId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.payoutDestinations });
      // Removing the destination a cash out was about to use changes whether
      // the form can be submitted at all.
      void queryClient.invalidateQueries({ queryKey: keys.redemptionEligibility });
    },
  });
}

/* ---- tax information ----------------------------------------------- */

export function useTaxInformation(enabled: boolean): UseQueryResult<TaxSummaryWire | null> {
  return useQuery({
    queryKey: keys.taxInformation,
    queryFn: ({ signal }) =>
      apiFetch<TaxSummaryWire | null>('/v1/creators/me/tax-information', { signal }),
    enabled,
  });
}

export function useSubmitTaxInformation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { kind: 'ssn' | 'ein' | 'itin'; value: string }) =>
      apiFetch<TaxSummaryWire>('/v1/creators/me/tax-information', {
        method: 'PUT',
        body: input,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.taxInformation });
      // Supplying it may be what unblocks a payout.
      void queryClient.invalidateQueries({ queryKey: keys.payoutEligibility });
      void queryClient.invalidateQueries({ queryKey: keys.earnings });
    },
  });
}

export function useForgetTaxInformation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<void>('/v1/creators/me/tax-information', { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.taxInformation });
      void queryClient.invalidateQueries({ queryKey: keys.payoutEligibility });
    },
  });
}

/* ---- tracked links -------------------------------------------------- */

export function useLinks(enabled: boolean): UseQueryResult<TrackedLinkWire[]> {
  return useQuery({
    queryKey: keys.links,
    queryFn: ({ signal }) => apiFetch<TrackedLinkWire[]>('/v1/creators/me/links', { signal }),
    enabled,
  });
}

export function useMintLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { merchantId: string; code: string; terms: SplitTermsWire }) =>
      apiFetch<TrackedLinkWire>('/v1/creators/me/links', { method: 'POST', body: input }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.links }),
  });
}
